import { useState } from 'react'
import { makeAssistantToolUI, type ToolCallMessagePartProps } from '@assistant-ui/react'

type AccountOption = {
  id: string
  label: string
  detail: string
  kind: 'ACCOUNT' | 'CARD' | 'UNSUPPORTED'
  enabled: boolean
  selected: boolean
  note?: string
}

type SelectAccountsArgs = { multiple: boolean; options: AccountOption[] }
type SelectAccountsResult = { accountIds: string[] }

/**
 * Which of the bank's accounts to bring in.
 *
 * Everything usable starts ticked, because wanting all of them is the common case and a
 * screen of empty boxes makes the easy path the most work. Accounts that cannot be imported
 * are still listed, greyed, with the reason — somebody hunting for their credit card should
 * learn that it is modelled separately rather than wonder why the bank reported five
 * accounts and Fluence offered three.
 */
export const SelectAccountsToolUI = makeAssistantToolUI<SelectAccountsArgs, SelectAccountsResult>({
  toolName: 'selectAccounts',
  render: SelectAccountsTool,
})

function SelectAccountsTool({
  args,
  status,
  result,
  addResult,
}: ToolCallMessagePartProps<SelectAccountsArgs, SelectAccountsResult>) {
  const options = args.options ?? []
  // Null until the user touches something, so the server's defaults apply without having
  // to synchronise state against props that arrive asynchronously.
  const [chosen, setChosen] = useState<Set<string> | null>(null)
  const waiting = status.type === 'requires-action'

  const selection =
    chosen ?? new Set(options.filter((o) => o.selected && o.enabled).map((o) => o.id))

  const toggle = (id: string) => {
    const next = new Set(selection)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setChosen(next)
  }

  if (result) {
    const kept = options.filter((o) => result.accountIds.includes(o.id))
    return (
      <p className="aui-note">
        {kept.length === 0 ? 'None selected.' : `Importing ${kept.map((o) => o.label).join(', ')}.`}
      </p>
    )
  }

  return (
    <div className="aui-choices">
      {options.map((option) => (
        <label key={option.id} className={`aui-check${option.enabled ? '' : ' is-disabled'}`}>
          <input
            type="checkbox"
            checked={option.enabled && selection.has(option.id)}
            disabled={!option.enabled || !waiting}
            onChange={() => toggle(option.id)}
          />
          <span className="aui-check__body">
            <span className="aui-choice__label">
              {option.label}
              {option.kind === 'CARD' && <span className="aui-tag">card</span>}
            </span>
            <span className="aui-choice__detail">
              {option.note ? `${option.detail} — ${option.note}` : option.detail}
            </span>
          </span>
        </label>
      ))}
      <div className="aui-action">
        <button
          type="button"
          className="btn-primary"
          disabled={!waiting}
          onClick={() => addResult({ accountIds: [...selection] })}
        >
          {selection.size === 0
            ? 'Skip for now'
            : `Import ${selection.size} account${selection.size === 1 ? '' : 's'}`}
        </button>
      </div>
    </div>
  )
}
